/**
 * Checkbox - Um componente de checkbox acessível, customizável e com suporte a mensagens de erro, sucesso e tooltip.
 *
 * @component
 * @example
 * // Uso básico
 * <Checkbox
 *   checked={isChecked}
 *   onChange={setIsChecked}
 *   label="Aceito os termos"
 * />
 *
 * @example
 * // Com mensagem de erro e helperText
 * <Checkbox
 *   checked={isChecked}
 *   onChange={setIsChecked}
 *   label="Aceito os termos"
 *   error={hasError}
 *   errorMessage="Você deve aceitar os termos"
 *   helperText="Obrigatório para continuar"
 * />
 *
 * @example
 * // Com ícones e tooltip
 * <Checkbox
 *   checked={isChecked}
 *   onChange={setIsChecked}
 *   label="Receber novidades"
 *   leftIcon={<FaBell />}
 *   rightIcon={<FaTimes />}
 *   onRightIconClick={() => alert('Ícone direito clicado!')}
 *   tooltip="Você pode ativar ou desativar notificações"
 * />
 *
 * @example
 * // Indeterminado (para seleção parcial)
 * <Checkbox
 *   checked={false}
 *   indeterminate={true}
 *   onChange={setIsChecked}
 *   label="Selecionar todos"
 * />
 *
 * @param {object} props - Propriedades do componente Checkbox.
 * @param {boolean} props.checked - Estado do checkbox (marcado ou não).
 * @param {(checked: boolean) => void} props.onChange - Callback chamado ao alterar o estado.
 * @param {string} [props.id] - ID do input (gerado automaticamente se não fornecido).
 * @param {string} [props.label] - Texto do label exibido ao lado do checkbox.
 * @param {boolean} [props.disabled=false] - Desabilita o checkbox.
 * @param {boolean} [props.readOnly=false] - Torna o checkbox somente leitura.
 * @param {boolean} [props.required=false] - Indica se o campo é obrigatório.
 * @param {"light"|"dark"} [props.theme="light"] - Tema visual.
 * @param {boolean} [props.error=false] - Indica estado de erro.
 * @param {string|function} [props.errorMessage] - Mensagem de erro (string ou função).
 * @param {string|function} [props.successMessage] - Mensagem de sucesso (string ou função).
 * @param {string} [props.helperText] - Texto de ajuda exibido abaixo do checkbox.
 * @param {React.ReactNode} [props.leftIcon] - Ícone à esquerda do checkbox.
 * @param {React.ReactNode} [props.rightIcon] - Ícone à direita do checkbox.
 * @param {() => void} [props.onRightIconClick] - Callback ao clicar no ícone direito.
 * @param {string} [props.tooltip] - Texto do tooltip de ajuda.
 * @param {string} [props.className] - Classe CSS adicional.
 * @param {object} [props.containerProps] - Props adicionais para o container.
 * @param {number} [props.tabIndex] - TabIndex do container.
 * @param {boolean} [props.autoFocus] - Foca automaticamente no componente ao montar.
 * @param {string} [props.accessibilityLabel] - Label acessível personalizado.
 * @param {string} [props.accessibilityErrorMessage] - Mensagem de erro acessível personalizada.
 * @param {CheckboxI18nContext} [props.i18nMessages] - Mensagens customizadas de internacionalização.
 * @param {string} [props.data-testid] - Test ID para testes automatizados.
 * @param {React.ReactNode} [props.children] - Elementos filhos adicionais.
 * @param {(event: React.FocusEvent<HTMLDivElement>) => void} [props.onFocus] - Callback de foco.
 * @param {(event: React.FocusEvent<HTMLDivElement>) => void} [props.onBlur] - Callback de perda de foco.
 * @param {boolean} [props.indeterminate=false] - Estado indeterminado (checkbox parcial).
 * @param {number} [props.successTimeoutMs=800] - Tempo em ms para exibir mensagem de sucesso.
 *
 * @returns {JSX.Element} O componente Checkbox renderizado.
 */

import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useId,
  useContext,
  useCallback,
} from "react";
import clsx from "clsx";
import { FaCheck, FaInfoCircle } from "react-icons/fa";
import Tooltip from "../Tooltip/Tooltip";

// Contexto de mensagens (pode ser compartilhado com Toggle)
export interface CheckboxI18nContext {
  getDefaultErrorMessage: () => string;
  getDefaultSuccessMessage?: () => string;
}
export const CheckboxMessagesContext = React.createContext<CheckboxI18nContext>({
  getDefaultErrorMessage: () => "Seleção inválida",
});

// Resolve mensagem (string ou função)
function resolveMessage(
  value: boolean,
  msg: string | ((value: boolean) => string) | undefined,
  fallback: () => string
): string {
  if (typeof msg === "function") return msg(value);
  if (msg !== undefined) return msg;
  return fallback();
}

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  theme?: "light" | "dark";
  error?: boolean;
  errorMessage?: string | ((checked: boolean) => string);
  successMessage?: string | ((checked: boolean) => string);
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  tooltip?: string;
  className?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement> & { "data-testid"?: string };
  tabIndex?: number;
  autoFocus?: boolean;
  accessibilityLabel?: string;
  accessibilityErrorMessage?: string;
  i18nMessages?: CheckboxI18nContext;
  "data-testid"?: string;
  children?: React.ReactNode;
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  indeterminate?: boolean;
  successTimeoutMs?: number;
}

const Checkbox = React.memo(
  forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
    const {
      checked,
      onChange,
      label,
      disabled = false,
      readOnly = false,
      required = false,
      theme = "light",
      error = false,
      errorMessage,
      successMessage,
      helperText,
      leftIcon,
      rightIcon,
      onRightIconClick,
      tooltip,
      className,
      containerProps,
      tabIndex,
      autoFocus,
      accessibilityLabel,
      accessibilityErrorMessage,
      i18nMessages,
      "data-testid": dataTestId,
      children,
      onFocus,
      onBlur,
      indeterminate = false,
      successTimeoutMs = 800,
    } = props;

    const [showSuccess, setShowSuccess] = useState(false);
    const wasErrorRef = useRef(false);
    const successHideTimeout = useRef<NodeJS.Timeout | null>(null);

    const generatedId = useId();
    const inputId = props.id || generatedId;

    const contextI18n = useContext(CheckboxMessagesContext);
    const i18n = i18nMessages || contextI18n;

    useEffect(() => {
      if (error) {
        wasErrorRef.current = true;
        setShowSuccess(false);
      }
    }, [error]);

    useEffect(() => {
      return () => {
        if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
      };
    }, []);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null); // Novo ref para o container

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Foco no input se autoFocus for true
    useEffect(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus();
      }
    }, [autoFocus]);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const handleToggle = useCallback((): void => {
      if (disabled || readOnly) return;
      onChange(!checked);
      // Só mostra sucesso se estava em erro antes
      if (wasErrorRef.current && !error) {
        setShowSuccess(true);
        wasErrorRef.current = false;
        if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
        successHideTimeout.current = setTimeout(() => setShowSuccess(false), successTimeoutMs);
      }
    }, [disabled, readOnly, onChange, checked, error, successTimeoutMs]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        handleToggle();
      }
    }, [handleToggle]);

    const handleFocus = useCallback((e?: React.FocusEvent<HTMLDivElement>): void => {
      onFocus?.(e as React.FocusEvent<HTMLDivElement>);
    }, [onFocus]);

    const handleBlur = useCallback((e?: React.FocusEvent<HTMLDivElement>): void => {
      onBlur?.(e as React.FocusEvent<HTMLDivElement>);
    }, [onBlur]);

    const containerClass = clsx(
      "flex items-center gap-3 mb-4 relative outline-none select-none",
      theme === "dark" ? "text-gray-100" : "text-gray-900",
      disabled && "opacity-70 pointer-events-none",
      error
        ? "ring-2 ring-red-500"
        : showSuccess
        ? "ring-2 ring-green-600"
        : "",
      containerProps?.className ?? className
    );

    // Tailwind/Fluent UI color tokens
    // Você pode customizar esses nomes no seu tailwind.config.js para ficar 100% fiel ao design system
    const baseBox =
      "w-[18px] h-[18px] rounded-[4px] flex items-center justify-center transition-colors duration-150 border-2 relative";
    const baseIcon = "w-[18px] h-[18px] text-base flex items-center justify-center"; // removido transition-colors

    // Estados do container
    const boxClass = clsx(
      baseBox,
      disabled
        ? [
            checked || indeterminate
              ? "bg-neutral-200 border-neutral-200 dark:bg-neutral-800 dark:border-neutral-800 opacity-40"
              : "border-neutral-400 bg-transparent dark:border-neutral-700 opacity-40",
          ]
        : error
        ? checked || indeterminate
          ? "bg-red-600 border-red-600"
          : "border-red-600 bg-transparent"
        : checked || indeterminate
        // FLUENT UI PRIMARY
        ? "bg-[#0078D4] border-[#0078D4]"
        : "border-neutral-400 bg-transparent dark:border-neutral-500",
      // Hover
      !disabled &&
        !error &&
        (checked || indeterminate)
        ? "hover:bg-[#005A9E] hover:border-[#005A9E]"
        : !disabled && !error
        ? "hover:border-neutral-900 dark:hover:border-neutral-100"
        : "",
      // Error hover
      !disabled && error
        ? checked || indeterminate
          ? "hover:bg-red-700 hover:border-red-700"
          : "hover:border-red-700"
        : ""
    );

    const iconClass = clsx(
      baseIcon,
      disabled
        ? checked || indeterminate
          ? "text-neutral-50 opacity-40"
          : "text-[#0078D4] opacity-40"
        : error
        ? checked || indeterminate
          ? "text-white"
          : "text-red-600"
        : checked || indeterminate
        // FLUENT UI CONTRASTE
        ? "text-white"
        : "text-[#201F1E] dark:text-neutral-100",
      !disabled && !error
        ? "group-hover:text-[#201F1E] dark:group-hover:text-neutral-100"
        : "",
      !disabled && error
        ? checked || indeterminate
          ? ""
          : "group-hover:text-red-700"
        : ""
    );

    function getAriaChecked(indeterminate: boolean, checked: boolean) {
      return indeterminate ? "mixed" : checked;
    }

    // Defina o valor final do data-testid
    const finalTestId =
      dataTestId ||
      (containerProps && containerProps["data-testid"]) ||
      "checkbox";

    return (
      <div
        {...containerProps}
        ref={containerRef}
        className={containerClass}
        // Remova o tabIndex do container!
        // tabIndex={tabIndex}
        data-testid={finalTestId}
        role="checkbox"
        aria-checked={getAriaChecked(indeterminate, checked)}
        aria-disabled={disabled}
        aria-readonly={readOnly}
        aria-required={required}
        aria-label={
          error
            ? resolveMessage(
                checked,
                accessibilityErrorMessage || errorMessage,
                i18n.getDefaultErrorMessage
              )
            : accessibilityLabel || label || "Selecionar"
        }
        aria-invalid={error}
        aria-describedby={helperText ? `${inputId}-helper` : undefined}
        // Remova onKeyDown do container!
        // onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <label
          htmlFor={inputId}
          className={clsx(
            "flex items-center font-medium text-base cursor-pointer w-full gap-1",
            theme === "dark" ? "text-gray-100" : "text-gray-900",
            disabled && "cursor-not-allowed"
          )}
          style={{ marginRight: "0.5rem" }}
        >
          {leftIcon && (
            <span className="flex items-center mr-1 text-lg">{leftIcon}</span>
          )}
          <div className={clsx(boxClass, !disabled && "cursor-pointer")}>
            <input
              ref={inputRef}
              id={inputId}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              aria-hidden="false"
              className="absolute w-full h-full top-0 left-0 opacity-0 appearance-none m-0 p-0"
              aria-checked={getAriaChecked(indeterminate, checked)}
              tabIndex={tabIndex ?? 0}
              onChange={e => {
                if (!disabled && !readOnly) onChange(e.target.checked);
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
            <span className={iconClass}>
              {indeterminate ? (
                <span className="block w-3 h-0.5 rounded bg-white dark:bg-neutral-900" />
              ) : checked ? (
                <FaCheck />
              ) : null}
            </span>
          </div>
          {label && (
            <span className="ml-2">
              {label}
              {required && (
                <span className="text-red-600 ml-1 text-base">*</span>
              )}
            </span>
          )}
          {tooltip && (
            <Tooltip
              tooltip={tooltip}
              placement="top"
              tabIndex={0}
              ariaLive="off"
              data-testid={`checkbox-tooltip-${inputId}`}
            >
              <FaInfoCircle aria-hidden="true" focusable="false" />
            </Tooltip>
          )}
          {rightIcon && (
            <span
              className={clsx(
                "flex items-center ml-1 text-lg cursor-pointer transition-colors",
                theme === "dark"
                  ? "text-gray-400 hover:text-blue-500 focus:text-blue-500"
                  : "text-gray-400 hover:text-blue-600 focus:text-blue-600"
              )}
              onClick={e => {
                e.stopPropagation();
                onRightIconClick?.();
              }}
              role="button"
              aria-label="Ação do ícone direito"
              tabIndex={0}
            >
              {rightIcon}
            </span>
          )}
        </label>
        {helperText && !error && !showSuccess && (
          <p
            className={clsx(
              "text-sm mt-0.5 ml-2",
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            )}
            id={`${inputId}-helper`}
            aria-live="polite"
            role="status"
          >
            {helperText}
          </p>
        )}
        {error ? (
          <p className="text-sm text-red-600 mt-0.5 ml-2" aria-live="assertive" role="alert">
            {resolveMessage(checked, errorMessage, i18n.getDefaultErrorMessage)}
          </p>
        ) : showSuccess && successMessage ? (
          <p className="text-sm text-green-700 mt-0.5 ml-2 flex items-center gap-1" aria-live="polite" role="status">
            <FaCheck />{" "}
            {resolveMessage(
              checked,
              successMessage,
              i18n.getDefaultSuccessMessage || (() => "Alterado com sucesso")
            )}
          </p>
        ) : null}
        {children}
      </div>
    );
  })
);

Checkbox.displayName = "Checkbox";
export default Checkbox;